import React, {
  Children,
  ReactNode,
  cloneElement,
  isValidElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { BlanksContext, BlanksState } from "./Blank";
import { Button, ButtonGroup } from "@chakra-ui/react";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  closestCorners,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { FiRotateCcw } from "react-icons/fi";
import { RiDragDropLine } from "react-icons/ri";
import {
  TaskContent,
  TaskEvent,
  TaskStatus,
  TaskStatusProps,
  TaskWrapper,
  useFlash,
  useTask,
  useTrials,
} from "../Task";
import { Word } from "./Word";
import { WordBank } from "./WordBank";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { isEqual, shuffle, uniq } from "lodash";
import { useConfetti } from "@utils";

type FillInTheBlanksProps = {
  children: ReactNode;
  taskId: string;
  title?: string;
  wrongAnswers?: string[];
} & Pick<TaskStatusProps, "successMessage" | "failureMessage">;

export function FillInTheBlanks({
  children,
  taskId,
  title = "Drag 'n' Drop",
  wrongAnswers = [],
  ...taskStatusProps
}: FillInTheBlanksProps) {
  const { task, saveTask, savingTask } = useTask(taskId);
  const flash = useFlash();

  const { trialsRemaining, resetTrials } = useTrials();

  const [submitButtonRef, confetti] = useConfetti<HTMLButtonElement>();

  const { allChildren, solutions, words } = useMemo(() => {
    const solutions: string[][] = [];

    const allChildren = Children.toArray(children).map((child, index) => {
      if (isValidElement(child) && child.props.mdxType === "Blank") {
        const blankIndex = solutions.length;

        const { solution } = child.props;
        solutions.push(Array.isArray(solution) ? solution : [solution]);

        return cloneElement(child, { id: blankIndex });
      }

      return (
        <TaskContent preserveSpaces key={index}>
          {child}
        </TaskContent>
      );
    });

    return {
      allChildren,
      solutions,
      words: shuffle(uniq(solutions.concat(wrongAnswers).flat())),
    };
  }, [children, wrongAnswers]);

  const [sortableWords, setSortableWords] = useState(words);

  const completedState = useMemo<BlanksState>(
    () =>
      solutions.reduce<BlanksState>((acc, solution, index) => {
        const wordsUsed = Object.values(acc).map((blank) => blank.value);
        return {
          ...acc,
          [index]: {
            value: solution.find((word) => !wordsUsed.includes(word)),
            correct: true,
          },
        };
      }, {}),
    [solutions]
  );

  const [blanks, setBlanks] = useState<BlanksState>(
    task?.completedAt ? completedState : {}
  );

  const isHydrated = useRef(false);

  useEffect(() => {
    // set the completed state using an effect in case the task data is loaded
    // after the component is mounted
    if (task?.completedAt && !isHydrated.current) {
      setBlanks(completedState);
      setSubmitted(true);
      isHydrated.current = true;
    }
  }, [task, completedState]);

  const [submitted, setSubmitted] = useState(task?.completedAt ? true : false);

  const isCorrect = useMemo(
    () => solutions.every((_, index) => blanks[index]?.correct),
    [solutions, blanks]
  );

  const wordsUsed = useMemo(
    () => Object.entries(blanks).map(([, { value }]) => value),
    [blanks]
  );

  const mouseSensor = useSensor(MouseSensor);
  const keyboardSensor = useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates,
  });

  const sensors = useSensors(mouseSensor, keyboardSensor);

  const [activeId, setActiveId] = useState<string | null>(null);

  return (
    <TaskWrapper taskId={taskId} title={title} icon={RiDragDropLine} {...flash}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={({ active }) => setActiveId(active.id.toString())}
        onDragEnd={({ over, active }) => {
          setActiveId(null);

          if (!over || over.id === active.id) {
            return;
          }

          const overId =
            typeof over.id === "number"
              ? over.id.toString()
              : Object.entries(blanks).find(
                  ([, { value }]) => value === over.id
                )?.[0];

          const nextBlanks = { ...blanks };
          for (const blankId in nextBlanks) {
            if (nextBlanks[blankId].value === active.id.toString()) {
              delete nextBlanks[blankId];
            }
          }

          if (!overId) {
            // if the word is being dragged out of a blank/into the word bank...
            setSortableWords((prev) => {
              const oldIndex = prev.indexOf(active.id.toString());
              const newIndex = prev.indexOf(over.id.toString());
              return arrayMove(prev, oldIndex, newIndex);
            });
          } else {
            // we're dropping a word into a blank
            nextBlanks[overId] = {
              value: active.id.toString(),
            };
          }

          setBlanks(nextBlanks);

          if (!isEqual(blanks, nextBlanks)) {
            setSubmitted(false);
          }
        }}
      >
        <BlanksContext.Provider value={{ blanks, solutions }}>
          <div>{allChildren}</div>
        </BlanksContext.Provider>
        <WordBank
          words={sortableWords.filter((word) => !wordsUsed.includes(word))}
        />
        <DragOverlay>{activeId && <Word>{activeId}</Word>}</DragOverlay>
      </DndContext>
      {!(submitted && isCorrect) && (
        <ButtonGroup mt="3">
          <Button
            ref={submitButtonRef}
            isLoading={savingTask}
            isDisabled={!wordsUsed.length}
            onClick={() => {
              let isCorrect = Object.keys(blanks).length === solutions.length;
              const nextBlanks = { ...blanks };

              for (const blankId in nextBlanks) {
                const isBlankCorrect = solutions[Number(blankId)].includes(
                  nextBlanks[blankId].value
                );
                nextBlanks[blankId].correct = isBlankCorrect;

                if (!isBlankCorrect) {
                  isCorrect = false;
                }
              }

              if (isCorrect) {
                confetti();
              }

              setBlanks(nextBlanks);
              setSubmitted(true);
              resetTrials(isCorrect);
              saveTask(isCorrect ? new Date() : null);

              window.gtag?.("event", isCorrect ? "dnd_pass" : "dnd_fail", {
                task_id: taskId,
                response: blanks,
              });
            }}
          >
            Submit
          </Button>
          {submitted && (
            <Button
              isDisabled={savingTask}
              leftIcon={<FiRotateCcw />}
              onClick={() => {
                setSubmitted(false);
                setBlanks({});
                resetTrials();
              }}
            >
              Reset
            </Button>
          )}
          {trialsRemaining <= 0 && (
            <Button
              isDisabled={savingTask}
              colorScheme="green"
              onClick={() => {
                const correctAnswers = solutions.reduce(
                  (acc, solution, index) => {
                    const word = blanks[index];
                    if (word && solution.includes(word.toString())) {
                      return { ...acc, [index]: word };
                    }

                    const wordsUsed = Object.values(acc);
                    const answer = solution.find(
                      (word) => !wordsUsed.includes(word)
                    );

                    return answer
                      ? {
                          ...acc,
                          [index]: {
                            value: answer,
                            correct: true,
                          },
                        }
                      : acc;
                  },
                  {}
                );

                setBlanks(correctAnswers);
                setSubmitted(true);
                resetTrials();
                confetti();
                saveTask(new Date());

                window.gtag?.("event", "dnd_solution");
              }}
            >
              Show solution
            </Button>
          )}
        </ButtonGroup>
      )}
      {submitted && (
        <TaskStatus
          isCorrect={isCorrect}
          trialsRemaining={trialsRemaining}
          {...taskStatusProps}
        />
      )}
      <TaskEvent taskId={taskId} isComplete={submitted && isCorrect} />
    </TaskWrapper>
  );
}
